import CommonCoupon from 'common/components/entities/Coupon'
import LimitedSubscriptionPlan from 'common/components/entities/OfferPriceNew/PricePlan/LimitedSubscriptionPlan'
import SubscriptionPlan from 'common/components/entities/OfferPriceNew/PricePlan/SubscriptionPlan'
import { CouponTypeEnum } from 'common/enums/CouponTypeEnum'
import { CouponInterface } from 'src/shared/entities/coupon/coupon.schema'
import { centsToEuro, getReadablePrice } from 'common/utils/price-calculator'
import { checkAndFetchCoupon } from 'publisher/api/payment'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import { useManagement, usePage, usePayment } from 'publisher/store'
import managementSelectors from 'publisher/store/management/managementSelectors'
import pageSelectors from 'publisher/store/page/pageSelectors'
import {
  checkedCouponFail,
  checkedCouponSuccess,
  updateCoupon,
} from 'publisher/store/payment/paymentActions'
import {
  getCheckedCoupon,
  getCheckedPricePlan,
  getCouponCode,
  getCouponError,
  getPricePlans,
  getProduct,
} from 'publisher/store/payment/paymentSelectors'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

interface CouponProps {
  entity: CouponInterface
}

export default function Coupon({ entity }: CouponProps) {
  const [isChecking, setIsChecking] = useState(false)

  const dispatch = useDispatch()

  const couponCode = usePayment(getCouponCode)
  const checkedCoupon = usePayment(getCheckedCoupon)
  const couponError = usePayment(getCouponError)
  const checkedPricePlan = usePayment(getCheckedPricePlan)
  const pricePlans = usePayment(getPricePlans)
  const product = usePayment(getProduct)
  const funnelStepId = useManagement(managementSelectors.getFunnelStepId)

  const locale = usePage(pageSelectors.getLocale)

  const handleCheck = useCallback(async () => {
    setIsChecking(true)

    const { data } = await checkAndFetchCoupon(funnelStepId, couponCode)
    if (data.success) {
      dispatch(checkedCouponSuccess(data.coupon))
    } else {
      dispatch(checkedCouponFail(data.error))
    }
    setIsChecking(false)
  }, [funnelStepId, couponCode])

  const updateCouponCode = useCallback((value: string) => {
    dispatch(updateCoupon(value))
  }, [])

  const { appearance, margin, mobileMargin } = entity

  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  if (pricePlans.length === 0 && !product) {
    return (
      isClient && (
        <StyleWrapper
          margin={margin}
          mobileMargin={mobileMargin}
          appearance={appearance}
        />
      )
    )
  }

  // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
  let discount
  if (pricePlans.length > 0) {
    discount = checkedCoupon
      ? checkedCoupon.discountType === CouponTypeEnum.Fixed
        ? getReadablePrice(
            centsToEuro(checkedCoupon.discountAmount),
            pricePlans[0].currency,
            locale,
          )
        : `${checkedCoupon.discountAmount}%`
      : ''
  }

  if (product) {
    discount = checkedCoupon
      ? checkedCoupon.discountType === CouponTypeEnum.Fixed
        ? getReadablePrice(
            centsToEuro(checkedCoupon.discountAmount),
            product.currency,
            locale,
          )
        : `${checkedCoupon.discountAmount}%`
      : ''
  }

  let discountDetails: ReactNode = null

  if (discount) {
    if (
      checkedPricePlan &&
      'subscriptionPlan' in checkedPricePlan &&
      checkedPricePlan?.subscriptionPlan
    ) {
      if (checkedPricePlan?.isLimited) {
        discountDetails = (
          <LimitedSubscriptionPlan
            amountWithCurrency={discount}
            intervalCount={checkedPricePlan.subscriptionPlan.intervalCount}
            interval={checkedPricePlan.subscriptionPlan.interval}
            limitOfPayments={checkedPricePlan.subscriptionPlan.limitOfPayments}
          />
        )
      } else {
        discountDetails = (
          <SubscriptionPlan
            amountWithCurrency={discount}
            intervalCount={checkedPricePlan.subscriptionPlan.intervalCount}
            interval={checkedPricePlan.subscriptionPlan.interval}
          />
        )
      }
    } else {
      discountDetails = discount
    }
  }

  return (
    isClient && (
      <StyleWrapper
        margin={margin}
        mobileMargin={mobileMargin}
        appearance={appearance}
      >
        <CommonCoupon
          button={entity.button}
          field={entity.field}
          htmlAttrId={entity.htmlAttrId}
          isChecking={isChecking}
          onCodeChange={updateCouponCode}
          onCheck={handleCheck}
          couponCode={couponCode}
          couponError={couponError}
          discount={discountDetails}
        />
      </StyleWrapper>
    )
  )
}
